import React, { useState, useEffect } from 'react';
import fr from 'facet-rest';
import {
  useManagedAccountPageContext,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Context/ManagedAccountPageContext';
import {
  ManagedAccountTaxDocumentsContextProvider,
} from 'Components/UI/Accounts/ManagedAccount/Context/ManagedAccountTaxDocumentsContext';
import {
  ManagedAccountTradeConfirmationDocumentsContextProvider,
} from 'Components/UI/Accounts/ManagedAccount/Context/ManagedAccountTradeConfirmationDocumentsContext';
import {
  ManagedAccountStatementDocumentsContextProvider,
} from 'Components/UI/Accounts/ManagedAccount/Context/ManagedAccountStatementDocumentsContext';
import StatementsTaxDocsTabContent from 'Components/UI/Accounts/ManagedAccount/Tabs/StatementsTaxDocsTab';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getSectionCardStyle } from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/styles';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = buildUseStyles('ManagedAccountDocumentsTabPanelLayout', (theme) => ({
  root: {
    ...getSectionCardStyle({ theme }),
  },
}));

async function fetchManagedAccountDocumentsTabData(resource) {
  // do nothing if the self link on the resource does not exist
  if (!resource?.link('self')) return {};

  const [managedAccountDocuments] = await resource.link('self').get();

  return {
    managedAccountDocuments,
  };
}

function useManagedAccountDocuments(resource) {
  const { displayErrorMessage } = useSnackbarContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(null);
  const [statementsLinkURL, setStatementsLinkURL] = useState(null);
  const [taxDocumentsLinkURL, setTaxDocumentsLinkURL] = useState(null);
  const [tradeConfirmationDocumentsLinkURL, setTradeConfirmationDocumentsLinkURL] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async function fetchData() {
      setIsLoading(true);
      setIsError(null);

      try {
        const {
          managedAccountDocuments,
        } = await fetchManagedAccountDocumentsTabData(resource);

        if (!ignore) {
          setStatementsLinkURL(managedAccountDocuments?.relationship(fr.relationships.statements.name)?.link('related').url());
          setTaxDocumentsLinkURL(managedAccountDocuments?.relationship(fr.relationships.taxDocuments.name)?.link('related').url());
          setTradeConfirmationDocumentsLinkURL(managedAccountDocuments?.relationship(fr.relationships.tradeConfirmationDocuments.name)?.link('related').url());
        }
      } catch (e) {
        setIsError(e || true);
        displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'Unable to fetch account documents');
      }

      setIsLoading(false);
    }());

    return () => {
      ignore = true;
    };
  }, [resource]);

  return {
    isLoading,
    isError,
    statementsLinkURL,
    taxDocumentsLinkURL,
    tradeConfirmationDocumentsLinkURL,
  };
}

export default function ManagedAccountDocumentsTabPanelLayout() {
  const { currentTabResource } = useManagedAccountPageContext();
  const classes = useStyles();


  const {
    isLoading,
    isError,
    statementsLinkURL,
    taxDocumentsLinkURL,
    tradeConfirmationDocumentsLinkURL,
  } = useManagedAccountDocuments(currentTabResource);

  if (isError) {
    return <Box><Subtitle value="Could not load the information. Please retry later." /></Box>;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Box className={classes.root}>
      <ManagedAccountStatementDocumentsContextProvider statementDocumentsSelfLink={statementsLinkURL}>
        <ManagedAccountTaxDocumentsContextProvider taxDocumentsSelfLink={taxDocumentsLinkURL}>
          <ManagedAccountTradeConfirmationDocumentsContextProvider tradeConfirmationDocumentsSelfLink={tradeConfirmationDocumentsLinkURL}>
            <StatementsTaxDocsTabContent />
          </ManagedAccountTradeConfirmationDocumentsContextProvider>
        </ManagedAccountTaxDocumentsContextProvider>
      </ManagedAccountStatementDocumentsContextProvider>
    </Box>
  );
}
